import React, { Component } from "react";
import { observer } from "mobx-react";
import { getRequest } from "../../utils/httpRequest";
import { AxiosResponse } from "axios";
import NewReviewModal from "../Enrolment/NewReviewModal";
import {
  EnrolmentsResource,
  OngoingCourse,
} from "../../stores/EnrolmentStore/Types";

interface Props {
  userIsInUk: boolean;
}

interface State {
  completedCourses: EnrolmentsResource;
  loaded: boolean;
  canScrollNext: boolean;
  canScrollPrev: boolean;
  selectedReviewCourse: OngoingCourse | null;
  showReviewModal: boolean;
}

const sortByCompletedAt = (courseA: OngoingCourse, courseB: OngoingCourse) => {
  const dateA = Date.parse(courseA.completed_at || "") || 0;
  const dateB = Date.parse(courseB.completed_at || "") || 0;

  return dateB - dateA;
};

class CompletedCourses extends Component<Props, State> {
  railRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      completedCourses: [],
      loaded: false,
      canScrollNext: false,
      canScrollPrev: false,
      selectedReviewCourse: null,
      showReviewModal: false,
    };
  }

  componentDidMount() {
    return this.fetchCompletedCourses();
  }

  fetchCompletedCourses = () => {
    return getRequest("/api/profile/completed.json").then(
      (response: AxiosResponse<EnrolmentsResource>) => {
        const sortedCourses = [...response.data].sort(sortByCompletedAt);

        this.setState(
          { completedCourses: sortedCourses, loaded: true },
          this.updateScrollState,
        );
      },
    );
  };

  scrollRail = (direction: "next" | "prev") => {
    const rail = this.railRef.current;

    if (!rail) return;

    const firstCard = rail.querySelector<HTMLElement>(
      ".completed-courses__card",
    );
    const cardWidth = firstCard ? firstCard.offsetWidth + 20 : 340;

    rail.scrollBy({
      left: direction === "next" ? cardWidth * 2 : cardWidth * -2,
      behavior: "smooth",
    });
  };

  updateScrollState = () => {
    const rail = this.railRef.current;

    if (!rail) return;

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;

    this.setState({
      canScrollPrev: rail.scrollLeft > 0,
      canScrollNext: rail.scrollLeft < maxScrollLeft - 1,
    });
  };

  courseUrl = (enrolment: OngoingCourse) =>
    enrolment.embedded_host_url
      ? enrolment.embedded_host_url
      : `/enrolments/${enrolment.id}`;

  courseImage = (userIsInUk, enrolment: OngoingCourse) => {
    if (userIsInUk && enrolment?.course?.preview_headshot_alt_image_url) {
      return enrolment?.course?.preview_headshot_alt_image_url;
    }

    return enrolment?.course?.preview_headshot_image_url;
  };

  courseImageAlt = (enrolment: OngoingCourse) =>
    enrolment.course.preview_masthead_alt_image_url ||
    `${enrolment.course.name} course thumbnail`;

  passedDate = (enrolment: OngoingCourse) => {
    if (!enrolment.completed_at) return "Passed";

    return `Passed ${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(enrolment.completed_at))}`;
  };

  fullStars = (enrolment) => Math.round(enrolment.course.rating);

  openReviewModal = (
    event: React.MouseEvent<HTMLButtonElement>,
    enrolment: OngoingCourse,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!enrolment.can_leave_review) return;

    this.setState({
      selectedReviewCourse: enrolment,
      showReviewModal: true,
    });
  };

  closeReviewModal = (event?: any) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.setState({
      selectedReviewCourse: null,
      showReviewModal: false,
    });
  };

  markCourseReviewed = () => {
    const { completedCourses, selectedReviewCourse } = this.state;

    if (!selectedReviewCourse) return;

    this.setState({
      completedCourses: completedCourses.map((enrolment) =>
        enrolment.id === selectedReviewCourse.id
          ? { ...enrolment, can_leave_review: false }
          : enrolment,
      ),
    });
  };

  openCourse = (enrolment: OngoingCourse) => {
    window.location.href = this.courseUrl(enrolment);
  };

  openCourseOnKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    enrolment: OngoingCourse,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    this.openCourse(enrolment);
  };

  render() {
    const { userIsInUk } = this.props;

    const {
      canScrollNext,
      canScrollPrev,
      completedCourses,
      loaded,
      selectedReviewCourse,
      showReviewModal,
    } = this.state;

    if (loaded && completedCourses.length === 0) return null;

    return (
      <div className="completed-courses" id="completed-courses">
        {!loaded && (
          <section
            className="completed-courses__section container"
            aria-labelledby="passed-courses-loading-heading"
          >
            <div className="completed-courses__top">
              <div>
                <h2
                  className="section-heading"
                  id="passed-courses-loading-heading"
                >
                  Courses you've passed
                </h2>
                <p className="section-sub">Loading your passed courses</p>
              </div>
            </div>
            <div
              className="profile-section-loader profile-section-loader--carousel"
              aria-live="polite"
            >
              <div className="profile-section-loader__card" />
              <div className="profile-section-loader__card" />
              <div className="profile-section-loader__card" />
            </div>
          </section>
        )}

        {loaded && completedCourses.length > 0 && (
          <section
            className="completed-courses__section container"
            aria-labelledby="passed-courses-heading"
          >
            <div className="completed-courses__top">
              <div>
                <h2 className="section-heading" id="passed-courses-heading">
                  Courses you've passed
                </h2>
                <p className="section-sub">
                  Tap any to revisit your answers or grab the certificate.
                </p>
              </div>
            </div>

            <div className="completed-courses__carousel">
              <button
                aria-label="Scroll passed courses back"
                className={`completed-courses__nav completed-courses__nav--prev ${
                  canScrollPrev ? "show" : ""
                }`}
                disabled={!canScrollPrev}
                onClick={() => this.scrollRail("prev")}
                type="button"
              >
                <span aria-hidden="true">&lsaquo;</span>
              </button>
              <button
                aria-label="Scroll passed courses forward"
                className={`completed-courses__nav completed-courses__nav--next ${
                  canScrollNext ? "show" : ""
                }`}
                disabled={!canScrollNext}
                onClick={() => this.scrollRail("next")}
                type="button"
              >
                <span aria-hidden="true">&rsaquo;</span>
              </button>

              <div
                className="completed-courses__rail"
                onScroll={this.updateScrollState}
                ref={this.railRef}
                tabIndex={0}
              >
                {completedCourses.map((enrolment) => (
                  <div
                    aria-label={`Open ${enrolment.course.name}`}
                    className="completed-courses__card"
                    key={enrolment.id}
                    onClick={() => this.openCourse(enrolment)}
                    onKeyDown={(event) =>
                      this.openCourseOnKeyDown(event, enrolment)
                    }
                    role="link"
                    tabIndex={0}
                  >
                    <div className="completed-courses__thumb">
                      <span className="completed-courses__tag">Passed</span>
                      <img
                        alt={this.courseImageAlt(enrolment)}
                        height="160"
                        loading="lazy"
                        src={this.courseImage(userIsInUk, enrolment)}
                        width="290"
                      />
                    </div>
                    <div className="completed-courses__body">
                      <h3 className="completed-courses__title">
                        {enrolment.course.name}
                      </h3>
                      <div className="completed-courses__passed-row">
                        <svg
                          aria-hidden="true"
                          className="completed-courses__passed-tick"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M3 8.5 L6.5 12 L13 4.5"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.2"
                          />
                        </svg>
                        <span>{this.passedDate(enrolment)}</span>
                      </div>
                      <div className="completed-courses__foot">
                        <div
                          aria-label={`Rated ${enrolment.course.rating} out of 5`}
                          className="completed-courses__stars"
                        >
                          {"★".repeat(this.fullStars(enrolment))}
                          {"☆".repeat(5 - this.fullStars(enrolment))}{" "}
                          <span>{enrolment.course.rating.toFixed(1)}</span>
                        </div>
                        <button
                          aria-disabled={!enrolment.can_leave_review}
                          className="completed-courses__more"
                          onClick={(event) =>
                            this.openReviewModal(event, enrolment)
                          }
                          tabIndex={enrolment.can_leave_review ? 0 : -1}
                          type="button"
                        >
                          {enrolment.can_leave_review ? "Review" : "Reviewed"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {selectedReviewCourse && (
          <NewReviewModal
            courseName={selectedReviewCourse.course.name}
            enrolmentId={selectedReviewCourse.id}
            handleHideNewReviewModal={this.closeReviewModal}
            refreshCertificate={this.markCourseReviewed}
            showModal={showReviewModal}
          />
        )}
      </div>
    );
  }
}
export default observer(CompletedCourses);
